import {Alert, Button, Code, Group, Stack, Text} from '@mantine/core';
import {useState} from 'react';
import {AiOutlineCheckCircle, AiOutlineCloseCircle} from 'react-icons/ai';
import {FlowResult} from '../types';
import {VerifyEventsPanel} from './VerifyEventsPanel';
import {useFlowClock} from '../hooks/useFlowClock';
import eventPipelineHealth from '../../../config/event-pipeline-health';

// Without a FlowClockProvider (not the tester's layout) the panel looks back
// this far instead of to the flow start.
const FALLBACK_WINDOW_MS = 5 * 60 * 1000;

interface FlowResultDisplayProps {
    result: FlowResult;
    onReset: () => void;
}

export const FlowResultDisplay = ({result, onReset}: FlowResultDisplayProps) => {
    const clock = useFlowClock();
    // Fixed at mount: a fresh Date per render would change the panel's query key.
    const [fallbackStart] = useState(() => new Date(Date.now() - FALLBACK_WINDOW_MS));
    const windowStart = clock?.startedAt ?? fallbackStart;

    const handleReset = () => {
        clock?.restart();
        onReset();
    };

    return (
        <Stack gap="md">
            <Alert
                icon={result.success ? <AiOutlineCheckCircle size={24}/> : <AiOutlineCloseCircle size={24}/>}
                title={result.success ? 'Flow Completed Successfully' : 'Flow Failed'}
                color={result.success ? 'teal' : 'red'}
            >
                {result.success ? (
                    <Stack gap="xs">
                        {result.status && <Text size="sm">Status: <Code>{result.status}</Code></Text>}
                        {result.intentId && <Text size="sm">Intent ID: <Code>{result.intentId}</Code></Text>}
                        {result.paymentMethodId &&
                            <Text size="sm">Payment Method: <Code>{result.paymentMethodId}</Code></Text>}
                        {result.subscriptionId &&
                            <Text size="sm">Subscription: <Code>{result.subscriptionId}</Code></Text>}
                    </Stack>
                ) : (
                    <Text size="sm">{result.error}</Text>
                )}
            </Alert>

            {eventPipelineHealth.showPanel && <VerifyEventsPanel windowStart={windowStart}/>}

            <Group>
                <Button variant="light" onClick={handleReset}>Start Over</Button>
            </Group>
        </Stack>
    );
};
